import {
  Component,
  inject,
  Input,
  signal,
  computed,
  effect,
  OnInit,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { FilterDialogComponent } from '../filter-dialog/filter-dialog.component';
import { ReferralListComponent } from '../../features/referrals/components/referral-list/referral-list.component';
import { NotificationDropdownComponent } from '../notification-dropdown/notification-dropdown.component';

import { TutorialService } from '../../services/tutorial.service';
import { ReferralQueryService } from '../../services/referral';
import { AuthService } from 'src/app/auth/services/auth.service';
import { LayoutService } from '../../services/layout.service';
import {
  PageHeaderComponent,
  FilterButtonComponent,
  CreateButtonComponent,
  RefreshButtonComponent,
} from '../../shared/components';
import { runWithRefreshIndicator } from '../../shared/utils/manual-refresh.util';
import { Logger } from '../../shared/utils/logger.util';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    BadgeModule,
    ToastModule,
    ReferralListComponent,
    FilterDialogComponent,
    PageHeaderComponent,
    FilterButtonComponent,
    CreateButtonComponent,
    RefreshButtonComponent,
  ],
  providers: [MessageService],
  template: `
    <div class="dashboard-container">
      <p-toast></p-toast>

      <!-- Page Header -->
      <app-page-header [title]="'Referrals (' + filteredTotal() + ')'">
        <app-refresh-button
          [iconOnly]="false"
          [loading]="isRefreshing()"
          (onClick)="onManualRefresh()"
        ></app-refresh-button>
        <app-filter-button (onClick)="openFilterDialog()"></app-filter-button>
        @if (authService.userPermissions()?.canCreateReferrals) {
          <app-create-button
            [label]="'NEW REFERRAL'"
            [routerLink]="'/referrals/create'"
          ></app-create-button>
        }
      </app-page-header>

      <!-- Filter Dialog -->
      <app-filter-dialog
        [visible]="filterDialogVisible()"
        [currentFilters]="referralQueryService.filters()"
        (filtersApplied)="onFiltersApplied($event)"
        (closed)="onFilterDialogClosed()"
      ></app-filter-dialog>

      <!-- Referrals Content -->
      <div class="referrals-content">
        <!-- Welcome Banner for First-Time Users -->
        @if (showWelcomeBanner()) {
          <div class="welcome-banner">
            <div class="banner-content">
              <i class="pi pi-graduation-cap welcome-icon"></i>
              <div class="banner-text">
                <h3 class="banner-title">Welcome to the ICS Referral Hub!</h3>
                <p class="banner-message">
                  @if (authService.userPermissions()?.canCreateReferrals) {
                    Here's how to get started: Create referrals using the "NEW
                    REFERRAL" button, filter existing referrals, and track their
                    status. Your referrals will be reviewed by the ICS team.
                  } @else {
                    Here's how to get started: Review incoming referrals,
                    filter the queue, and track their status updates across the
                    ICS workflow.
                  }
                </p>
              </div>
              <div class="banner-actions">
                <button class="get-started-btn" (click)="startTutorialFromBanner()">
                  <i class="pi pi-play"></i>
                  GET STARTED
                </button>
                <button class="close-banner-btn" (click)="closeBannerWithoutTutorial()" aria-label="Dismiss welcome banner">
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
          </div>
        }

        <!-- Success Banner -->
        @if (showSuccessBanner()) {
          <div class="success-banner">
            <div class="banner-content">
              <i class="pi pi-check-circle success-icon"></i>
              <div class="banner-text">
                <h3 class="banner-title">You have added a new referral.</h3>
                <p class="banner-message">
                  Patient Information has been shared to ICS team and will be
                  reviewed soon.
                </p>
              </div>
              <button class="dismiss-btn" (click)="dismissSuccessBanner()">
                <i class="pi pi-times"></i>
                DISMISS
              </button>
            </div>
          </div>
        }

        <app-referral-list
          [showHeader]="false"
          [externalRefreshing]="isRefreshing()"
          (totalReferralsChanged)="onTotalReferralsChanged($event)"
        ></app-referral-list>
      </div>
    </div>
  `,
  styles: [
    `
      .dashboard-container {
        background: var(--background-color);
        min-height: 100vh;
      }

      .referrals-content {
        background: var(--background-color);
        padding: 24px 24px 64px;
        min-height: 400px;
      }

      .referrals-content > * {
        max-width: var(--max-content-width);
        margin: 0 auto;
      }

      .patients-container {
        background-color: var(--background-card);
        border-radius: var(--border-radius);
        padding: 24px 0;
        border: 1px solid var(--border-color);
      }

      .patients-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1.5rem;
        padding: 0 24px;
      }

      .update-info {
        color: var(--text-muted);
        font-size: 0.9rem;
        display: flex;
        align-items: center;
      }

      .refresh-button {
        margin-left: 0.5rem;
        color: var(--text-heading);
        font-weight: 500;
      }

      .pagination-controls {
        display: flex;
        align-items: center;
      }

      .pagination-page {
        background-color: var(--btn-primary-bg);
        color: var(--btn-primary-text);
        padding: 0.5rem 0.8rem;
        border-radius: var(--border-radius);
        margin: 0 0.5rem;
        font-weight: 500;
      }

      .pagination-info {
        margin-left: 1.5rem;
        display: flex;
        align-items: center;
        color: var(--text-color);
      }

      .page-dropdown {
        margin: 0 0.5rem;
        width: 80px;
      }

      /* Success Banner */
      .success-banner {
        background: var(--accent-color-lighter);
        border: 1px solid var(--accent-color-light);
        border-radius: var(--border-radius-lg);
        margin-bottom: 24px;
        animation: slideDown 0.3s ease-out;
      }

      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .banner-content {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 20px 24px;
      }

      .success-icon {
        font-size: 2rem;
        color: var(--text-heading);
        flex-shrink: 0;
        margin-top: 4px;
      }

      .banner-text {
        flex: 1;
      }

      .banner-title {
        margin: 0 0 8px 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-heading);
      }

      .banner-message {
        margin: 0;
        font-size: 0.875rem;
        color: var(--text-heading);
        line-height: 1.5;
      }

      .dismiss-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: transparent;
        border: 1px solid var(--btn-primary-border);
        border-radius: var(--border-radius);
        color: var(--text-heading);
        font-weight: 600;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
      }

      .dismiss-btn:hover {
        background: var(--btn-primary-bg);
        color: var(--btn-primary-text);
      }

      /* Welcome Banner */
      .welcome-banner {
        background: #e9f4f8;
        border: 1px solid #52acc5;
        border-radius: var(--border-radius-lg);
        margin-bottom: 24px;
        animation: slideDown 0.3s ease-out;
      }

      .welcome-icon {
        font-size: 2rem;
        color: #0b435b;
        flex-shrink: 0;
        margin-top: 4px;
      }

      .welcome-banner .banner-title {
        margin: 0 0 8px 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: #0b435b;
      }

      .welcome-banner .banner-message {
        margin: 0;
        font-size: 0.875rem;
        color: #333333;
        line-height: 1.5;
      }

      .welcome-banner .banner-content {
        align-items: center;
      }

      .banner-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .get-started-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: #0b435b;
        border: 1px solid #0b435b;
        border-radius: var(--border-radius);
        color: white;
        font-weight: 600;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
      }

      .get-started-btn:hover {
        background: #0b738e;
        border-color: #0b738e;
      }

      .close-banner-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        background: transparent;
        border: 1px solid #52acc5;
        border-radius: var(--border-radius);
        color: #0b435b;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }

      .close-banner-btn:hover {
        background: #52acc5;
        color: white;
      }

      .welcome-banner .dismiss-btn {
        border-color: #52acc5;
        color: #0b435b;
      }

      .welcome-banner .dismiss-btn:hover {
        background: #52acc5;
        color: white;
      }
    `,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  layoutService = inject(LayoutService);

  referralQueryService = inject(ReferralQueryService);

  // export visible as an input
  @Input() visible = false;

  // Use signals for component state
  currentPage = signal(1);
  pageSize = signal(10); // Items per page
  lastUpdated = signal<Date>(new Date());
  filteredTotal = signal(0); // Total from filtered results
  filterDialogVisible = signal(false);
  showSuccessBanner = signal(false);
  showWelcomeBanner = signal(false);
  isRefreshing = signal(false);

  // Computed values based on filtered data
  totalPages = computed(() => {
    const total = this.filteredTotal();
    return Math.ceil(total / this.pageSize()) || 1;
  });

  pageOptions = computed(() => {
    const totalPgs = this.totalPages();
    return Array.from({ length: totalPgs }, (_, i) => ({
      label: (i + 1).toString(),
      value: i + 1,
    }));
  });

  private tutorialService = inject(TutorialService);
  public authService = inject(AuthService);

  constructor() {
    // Auto-dismiss welcome banner when a tutorial finishes or is exited
    effect(() => {
      const isActive = this.tutorialService.tutorialActive();
      if (!isActive && this.showWelcomeBanner()) {
        this.showWelcomeBanner.set(false);
        localStorage.setItem('ics-welcome-banner-dismissed', 'true');
      }
    });
  }

  ngOnInit(): void {
    this.layoutService.setMaxWidth('large');
    // Check if this is a first-time user and show welcome banner
    this.checkFirstTimeUser();

    // Check for success query parameters and show success banner
    this.route.queryParams.subscribe((params: { [key: string]: string }) => {
      // Handle webhook redirect after successful form submission
      // The aidbox-submit-callback redirects here with submitted=true&referralId=xxx
      if (params['submitted'] === 'true') {
        Logger.log(
          '✅ Webhook redirect received - referral submitted successfully',
          {
            referralId: params['referralId'],
          },
        );

        // Show success banner
        this.showSuccessBanner.set(true);

        // Show toast notification
        this.messageService.add({
          severity: 'success',
          summary: 'Referral Submitted Successfully',
          detail: params['referralId']
            ? `Referral ${params['referralId']} has been submitted and will be reviewed by the ICS team.`
            : 'Your referral has been submitted and will be reviewed by the ICS team.',
          life: 6000,
        });

        // Clean up URL parameters to prevent re-showing on refresh
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });

        // Refresh the referrals list to show the new referral
        this.refreshData();
        return;
      }

      if (params['success'] === 'true' && params['message']) {
        // Show success banner instead of toast
        this.showSuccessBanner.set(true);

        // Also show toast for backward compatibility
        this.messageService.add({
          severity: 'success',
          summary: 'New Referral Created',
          detail: params['message'],
          life: 5000,
        });
      }

      // Handle Microsoft OAuth callback errors
      if (params['error'] === 'user_deactivated') {
        Logger.log(
          'Microsoft OAuth: User deactivated, redirecting to deactivated page',
        );
        this.router.navigate(['/auth/account-deactivated']);
        return;
      }

      // Handle other Microsoft OAuth errors
      if (params['error']) {
        const errorMessage = params['message'] || 'Microsoft sign-in failed';
        this.messageService.add({
          severity: 'error',
          summary: 'Sign-In Failed',
          detail: errorMessage,
          life: 5000,
        });

        // Redirect to login after showing error
        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        }, 2000);
        return;
      }

      // Handle successful Microsoft OAuth authentication
      if (params['auth'] === 'success' && params['provider'] === 'microsoft') {
        this.messageService.add({
          severity: 'success',
          summary: 'Sign-In Successful',
          detail: 'You have been successfully signed in with Microsoft.',
          life: 3000,
        });
      }

      // Handle filtering from query parameters
      const hospitalId = params['hospitalId'];
      const programId = params['programId'];

      if (hospitalId || programId) {
        Logger.log('Dashboard: Applying filters from URL', {
          hospitalId,
          programId,
        });

        // Reset pagination
        this.currentPage.set(1);

        // Apply filters
        this.referralQueryService.setFilters({
          hospitalId: hospitalId || undefined,
          programId: programId || undefined,
          limit: this.pageSize(),
          offset: 0,
        });

        // Ensure data is refreshed
        this.refreshData();
      }
    });

    // Set initial pagination filters request if no filters were applied from URL
    // We check the route snapshot since the subscription might run later/async
    const snapshotParams = this.route.snapshot.queryParams;
    if (!snapshotParams['hospitalId'] && !snapshotParams['programId']) {
      Logger.log('Dashboard: Setting initial pagination filters (default)');
      this.updatePaginationFilters();
    }
  }

  ngOnDestroy(): void {
    this.layoutService.resetWidth();
  }

  refreshData(): void {
    // Refresh data through the referral service
    this.referralQueryService.invalidateAll();
    this.lastUpdated.set(new Date());
  }

  async onManualRefresh(): Promise<void> {
    await runWithRefreshIndicator(this.isRefreshing, () => this.refreshData());
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
      this.updatePaginationFilters();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
      this.updatePaginationFilters();
    }
  }

  onPageChange(event: any): void {
    // Page dropdown change handler
    this.currentPage.set(event.value);
    this.updatePaginationFilters();
  }

  onTotalReferralsChanged(total: number): void {
    Logger.log('Dashboard: Received total referrals changed:', total);
    this.filteredTotal.set(total);
  }

  private updatePaginationFilters(): void {
    const offset = (this.currentPage() - 1) * this.pageSize();
    const currentFilters = this.referralQueryService.filters();
    this.referralQueryService.setFilters({
      ...currentFilters,
      limit: this.pageSize(),
      offset: offset,
    });
  }

  onFiltersApplied(filters: any): void {
    // Reset to page 1 when filters change
    this.currentPage.set(1);
    this.referralQueryService.setFilters({
      ...filters,
      limit: this.pageSize(),
      offset: 0,
    });
  }

  onFilterRemoved(): void {
    // Reset to page 1 when a filter is removed
    this.currentPage.set(1);
    this.updatePaginationFilters();
  }

  onAllFiltersCleared(): void {
    // Reset to page 1 when all filters are cleared
    this.currentPage.set(1);
    this.updatePaginationFilters();
  }

  openFilterDialog(): void {
    this.filterDialogVisible.set(true);
  }

  onFilterDialogClosed(): void {
    this.filterDialogVisible.set(false);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.filterDialogVisible()) {
      this.filterDialogVisible.set(false);
      event.preventDefault();
    }
  }

  dismissSuccessBanner(): void {
    this.showSuccessBanner.set(false);
  }

  checkFirstTimeUser(): void {
    const welcomeBannerKey = 'ics-welcome-banner-dismissed';
    const hasDismissedWelcome = localStorage.getItem(welcomeBannerKey);
    const userId = this.authService.currentUser()?.id;
    const hasSeenTutorial = localStorage.getItem(
      'tutorial-completed-' + userId,
    );

    // Show welcome banner only — do NOT auto-start the tutorial
    if (!hasDismissedWelcome && !hasSeenTutorial && userId !== undefined) {
      this.showWelcomeBanner.set(true);
    }
  }

  startTutorialFromBanner(): void {
    this.showWelcomeBanner.set(false);
    localStorage.setItem('ics-welcome-banner-dismissed', 'true');
    this.tutorialService.startTutorial();
  }

  closeBannerWithoutTutorial(): void {
    this.showWelcomeBanner.set(false);
    localStorage.setItem('ics-welcome-banner-dismissed', 'true');
  }
}
